/**
 * Every user-facing string, in English and Assamese.
 *
 * NOTE FOR THE OWNER: the Assamese here is a first pass and needs a native
 * speaker to read it before 19 September. This is a memorial for an Assamese
 * icon; awkward Assamese would be worse than none. Every value below is safe
 * to edit in place — nothing parses these beyond lookup.
 */

export type Locale = "en" | "as";

export const LOCALES: Locale[] = ["en", "as"];

export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  as: "অসমীয়া",
};

type Strings = Record<string, string>;

const en: Strings = {
  // Opening film
  "intro.label": "Opening film",
  "intro.skip": "Skip and Enter",
  "intro.credit": "video by - jackson_chandon",

  // Join screen
  "join.date": "19 September 2026",
  "join.title": "Zubeen Da — Virtual Tribute",
  "join.subtitle": "Light a diya, leave a tribute, and watch together.",
  "join.nameLabel": "Name",
  "join.namePlaceholder": "Your name",
  "join.avatarLabel": "Choose your avatar",
  "join.submit": "Enter the venue",
  "join.submitBusy": "Entering…",
  "join.avatarAria": "Avatar",

  // Name validation
  "name.empty": "Please enter a name.",
  "name.too_long": "Names can be at most 20 characters.",
  "name.profanity": "Please choose a different name — this is a memorial.",
  "name.reserved": "That name is reserved. Please choose another.",
  "name.invalid_chars": "Please use letters or numbers in your name.",
  "join.failed": "Could not reach the venue. Check your connection and try again.",

  // Loading
  "loading.title": "Preparing the venue",
  "loading.ui": "Getting ready…",
  "loading.engine": "Loading the venue…",
  "loading.venue": "Lighting the lamps…",
  "loading.ready": "Ready",

  // Connection status
  "status.connecting": "Connecting to the venue…",
  "status.reconnecting": "Connection lost — reconnecting…",
  "status.disconnected": "Disconnected. Reload the page to rejoin.",
  "status.room_full": "The venue is full right now. Finding you another room…",
  "status.error": "Something went wrong reaching the venue.",

  // Unsupported browser
  "unsupported.title": "This browser cannot open the venue",
  "unsupported.body":
    "The venue needs a browser with WebGL. Please try Chrome or Safari, or update your browser.",

  // Zones
  "zone.stage": "Main Stage",
  "zone.memory_lane": "Memory Lane",
  "zone.diya": "Diya Courtyard",
  "zone.phool": "Phool Arpan",
  "zone.prayer": "Memory Gallery",
  "zone.singalong": "Sing-Along Pavilion",
  "zone.theatre": "Memories Theatre",
  "zone.radio": "Radio Garden",
  "zone.tribute": "Tribute Wall",
  "zone.entry": "Entry Gate",

  // Actions
  "action.diya": "🪔 Light a diya",
  "action.tribute": "✍️ Leave a tribute",
  "sound.off": "🔇 Tap for sound",
  "sound.on": "🔊 Sound on",
  "diya.already": "Your diya is already burning.",
  "diya.counter": "diyas lit tonight",
  "diya.counterOne": "diya lit tonight",

  // Stage screen
  "stage.countdown": "The tribute begins in",
  "stage.soon": "The tribute begins soon",
  "stage.note": "শ্ৰদ্ধাঞ্জলি · A tribute to Zubeen Garg",
  "stage.thanks": "ধন্যবাদ · Thank you for being here",
  "stage.linkNote": "Leave a lasting tribute at",

  // Tribute composer
  "tribute.title": "Leave a tribute",
  "tribute.subtitle": "Tributes are read by a moderator before they appear on the wall.",
  "tribute.placeholder": "A line for Zubeen Da…",
  "tribute.submit": "Send for review",
  "tribute.cancel": "Cancel",
  "tribute.queued": "Thank you. A moderator will review it shortly. 🪔",
  "tribute.empty": "Please write something first.",
  "tribute.too_long": "Tributes can be at most 120 characters.",
  "tribute.filtered": "Please rephrase this — it is a memorial wall.",
  "tribute.invalid_chars": "Please use words rather than only symbols.",
  "tribute.rate_limited": "You have just sent one. Give the moderators a moment.",
  "tribute.wrong_zone": "Stand by the tribute wall to leave a tribute.",
  "tribute.failed": "That could not be sent. Please try again.",

  // Settings
  "settings.open": "Settings",
  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.lowBandwidth": "Low-bandwidth mode",
  "settings.lowBandwidthHelp": "Fewer effects. Use this on a slow connection.",
  "settings.close": "Done",

  // Show phases
  "phase.preshow": "The gates are not open yet",
  "phase.gates_open": "The gates are open",
  "phase.opening_ceremony": "Opening ceremony",
  "phase.moment_of_silence": "A moment of silence",
  "phase.live": "Live now",
  "phase.closing_mayabini": "Mayabini",
  "phase.ended": "Thank you for coming",
  "phase.silenceNote": "Please stay still. Reactions are paused.",
  "phase.mayabiniNote": "The last song of the evening.",
  "phase.held": "Held by the organisers",

  // Phool Arpan
  "flower.offer": "Offer a flower",
  "flower.count": "flowers offered",
  "flower.countOne": "flower offered",
  "flower.wrong_zone": "Stand in Phool Arpan to offer a flower.",
  "flower.rate_limited": "One flower at a time.",

  // Seating
  "seat.sit": "Sit here",
  "seat.stand": "Stand up",
  "seat.occupied": "Someone is already sitting there.",
  "seat.too_far": "Step closer to the seat.",

  // Prayer
  "pray.start": "Bow your head",
  "pray.stop": "Rise",
  "pray.wrong_zone": "Enter the naam-ghor to pray.",

  // Sing-along
  "sing.raise": "Sing",
  "sing.voices": "voices singing",
  "sing.voiceOne": "voice singing",
  "sing.wrong_zone": "Stand in the pavilion to sing.",
  "sing.silence": "Not during the silence.",
  "sing.title": "Sing-Along",
  "sing.waiting": "The words appear when the music starts.",

  // Radio Garden
  "radio.title": "Radio Garden",
  "radio.nowPlaying": "Now playing",
  "radio.onAir": "On air",
  "radio.silence": "Silence",
  "radio.off": "Turn off",
  "radio.wrong_zone": "Stand in the garden to change the station.",
  "radio.rate_limited": "Someone just changed it. Give it a moment.",
  "radio.station.bihu": "Bihu",
  "radio.station.geet": "Geet",
  "radio.station.hindi": "Hindi",
  "radio.station.bhokti": "Bhokti Geet",
  "radio.station.borgeet": "Borgeet",
  "radio.unavailable": "This station has no recording loaded yet.",

  // Memories Theatre
  "theatre.title": "Memories Theatre",
  "theatre.upNext": "Up next",

  // Memory Lane
  "lane.title": "Memory Lane",

  // Direction map
  "map.open": "Map",
  "map.title": "Where you are",
  "map.you": "You",
  "map.close": "Close",
  "map.hint": "Tap a room to see the way.",
  "map.heading": "Head",

  // The Diya Portrait and the Eternal Choir (platform modules). These
  // surfaces only appear when their module is deployed.
  "portrait.title": "The portrait",
  "portrait.total": "diyas in the portrait",
  "portrait.totalOne": "diya in the portrait",
  "portrait.invite": "Every diya lit here becomes one part of his portrait.",
  "portrait.lit": "Your flame is",
  "portrait.already": "Your flame is already burning —",

  "choir.action": "Sing a line",
  "choir.title": "The Eternal Choir",
  "choir.loading": "Finding a line for you…",
  "choir.yourLine": "Line",
  "choir.alreadySinging": "already singing this",
  "choir.howto": "Read it once, then record. You can listen before you send.",
  "choir.record": "Record",
  "choir.stop": "Done",
  "choir.retake": "Record again",
  "choir.send": "Send my voice",
  "choir.sending": "Sending…",
  "choir.thanks": "Your voice is in the choir.",
  "choir.voiceNumber": "Voice",
  "choir.close": "Close",
  "choir.noMic": "The microphone was not allowed, so nothing was recorded.",
  "choir.rateLimited": "You have already sent a line. Come back a little later.",
  "choir.unavailable": "The choir is not reachable right now. Nothing was lost.",
  "choir.failed": "That did not send. Nothing was lost — try again in a moment.",

  "radio.module": "Zubeen Radio",
  "radio.listeners": "listening",
  "radio.upNext": "Next",
  "radio.tributeFrom": "Recorded by",

  // Voice
  "voice.ask": "Ask to talk",
  "voice.inviteTitle": "wants to talk",
  "voice.accept": "Accept",
  "voice.decline": "Decline",
  "voice.calling": "Asking to talk…",
  "voice.connected": "Talking with",
  "voice.end": "End",
  "voice.mute": "Mute",
  "voice.unmute": "Unmute",
  "voice.report": "Report",
  "voice.reported": "Reported. The call has ended.",
  "voice.declined": "They declined.",
  "voice.timeout": "No answer.",
  "voice.left": "They left.",
  "voice.busy": "One of you is already talking.",
  "voice.too_far": "Stand closer to ask.",
  "voice.denied": "Microphone permission is needed to talk.",
  "voice.failed": "The call could not connect.",
  "voice.consent": "Voice is one-to-one and only starts when both of you agree.",

  // Instagram embeds
  "ig.load": "Show this memory",
  "ig.loading": "Loading…",
  "ig.open": "Open on Instagram",
  "ig.failed": "This one could not be shown here. The link still works.",
  "ig.lowBandwidth": "Not loaded in low-bandwidth mode.",
  "ig.none": "No memories have been added yet.",
  "ig.prev": "Previous",
  "ig.next": "Next",
  "ig.credit": "Posted on Instagram by their owners.",
  // Shown in the world, under the photograph you are standing in front of.
  "ig.openInWorld": "Open this memory",
  "lane.photoAlt": "A memory of Zubeen Da, shared by a fan",
  "theatre.watch": "Watch this memory",
  "presence.one": "person here tonight",
  "presence.many": "people here tonight",
  "theatre.credit": "Sent in by fans on zubeengarg.xyz",
  "stage.sourceLabel": "Where these films come from",
  "stage.sourceNote":
    "Nothing here is re-uploaded — every film plays straight from its own YouTube channel, so the views go home.",
  "theatre.stop": "Stop",
  "lane.credit": "Shared by fans on zubeengarg.xyz",
  "ig.close": "Close",
};

const as: Strings = {
  // Opening film. The skip label stays English on purpose — the owner wants
  // the same two words on that button in either language.
  "intro.label": "আৰম্ভণিৰ ভিডিঅ'",
  "intro.skip": "Skip and Enter",
  // The handle is the credit; only the words around it are translated.
  "intro.credit": "ভিডিঅ' - jackson_chandon",

  // Join screen
  "join.date": "১৯ ছেপ্টেম্বৰ ২০২৬",
  "join.title": "জুবিন দা — ভাৰ্চুৱেল শ্ৰদ্ধাঞ্জলি",
  "join.subtitle": "এটি চাকি জ্বলাওক, শ্ৰদ্ধাঞ্জলি লিখক, আৰু একেলগে চাওক।",
  "join.nameLabel": "নাম",
  "join.namePlaceholder": "আপোনাৰ নাম",
  "join.avatarLabel": "আপোনাৰ অৱতাৰ বাছনি কৰক",
  "join.submit": "ভিতৰলৈ সোমাওক",
  "join.submitBusy": "সোমাই আছে…",
  "join.avatarAria": "অৱতাৰ",

  // Name validation
  "name.empty": "অনুগ্ৰহ কৰি এটা নাম লিখক।",
  "name.too_long": "নাম ২০টা আখৰতকৈ বেছি হ'ব নোৱাৰে।",
  "name.profanity": "অনুগ্ৰহ কৰি আন এটা নাম বাছনি কৰক — এইটো এটা শ্ৰদ্ধাঞ্জলি সভা।",
  "name.reserved": "এই নামটো সংৰক্ষিত। অনুগ্ৰহ কৰি আন এটা বাছনি কৰক।",
  "name.invalid_chars": "অনুগ্ৰহ কৰি নামত আখৰ বা সংখ্যা ব্যৱহাৰ কৰক।",
  "join.failed": "ভিতৰলৈ সোমাব পৰা নগ'ল। ইণ্টাৰনেট সংযোগ চাই পুনৰ চেষ্টা কৰক।",

  // Loading
  "loading.title": "ঠাইখন সাজু কৰি থকা হৈছে",
  "loading.ui": "সাজু হৈ আছে…",
  "loading.engine": "ঠাইখন লোড হৈ আছে…",
  "loading.venue": "চাকিবোৰ জ্বলোৱা হৈছে…",
  "loading.ready": "সাজু",

  // Connection status
  "status.connecting": "সংযোগ হৈ আছে…",
  "status.reconnecting": "সংযোগ বিচ্ছিন্ন হ'ল — পুনৰ সংযোগ হৈ আছে…",
  "status.disconnected": "সংযোগ বিচ্ছিন্ন হ'ল। পুনৰ সোমাবলৈ পৃষ্ঠাটো ৰিলোড কৰক।",
  "status.room_full": "এতিয়া ঠাই নাই। আপোনাৰ বাবে আন এটা কোঠা বিচাৰি থকা হৈছে…",
  "status.error": "ঠাইখনলৈ সংযোগ কৰোঁতে কিবা ভুল হ'ল।",

  // Unsupported browser
  "unsupported.title": "এই ব্ৰাউজাৰত ঠাইখন খুলিব নোৱাৰি",
  "unsupported.body":
    "ঠাইখনৰ বাবে WebGL থকা ব্ৰাউজাৰ লাগে। অনুগ্ৰহ কৰি Chrome বা Safari ব্যৱহাৰ কৰক, নতুবা ব্ৰাউজাৰটো আপডেট কৰক।",

  // Zones
  "zone.stage": "মুখ্য মঞ্চ",
  "zone.memory_lane": "সোঁৱৰণীৰ বাট",
  "zone.diya": "চাকিৰ চোতাল",
  "zone.phool": "ফুল অৰ্পণ",
  "zone.prayer": "স্মৃতিৰ চিত্ৰশালা",
  "zone.singalong": "গীতৰ মণ্ডপ",
  "zone.theatre": "স্মৃতিৰ প্ৰেক্ষাগৃহ",
  "zone.radio": "ৰেডিঅ’ বাগিচা",
  "zone.tribute": "শ্ৰদ্ধাঞ্জলিৰ দেৱাল",
  "zone.entry": "প্ৰৱেশ দুৱাৰ",

  // Actions
  "action.diya": "🪔 চাকি জ্বলাওক",
  "action.tribute": "✍️ শ্ৰদ্ধাঞ্জলি লিখক",
  "sound.off": "🔇 শব্দৰ বাবে টিপক",
  "sound.on": "🔊 শব্দ চালু",
  "diya.already": "আপোনাৰ চাকি ইতিমধ্যে জ্বলি আছে।",
  "diya.counter": "চাকি জ্বলিছে আজি",
  "diya.counterOne": "চাকি জ্বলিছে আজি",

  // Stage screen
  "stage.countdown": "শ্ৰদ্ধাঞ্জলি আৰম্ভ হ'ব",
  "stage.soon": "শ্ৰদ্ধাঞ্জলি অলপ পিছতে আৰম্ভ হ'ব",
  "stage.note": "শ্ৰদ্ধাঞ্জলি · জুবিন গাৰ্গলৈ",
  "stage.thanks": "ধন্যবাদ · আপুনি আহিল",
  "stage.linkNote": "স্থায়ী শ্ৰদ্ধাঞ্জলি ইয়াত লিখক",

  // Tribute composer
  "tribute.title": "শ্ৰদ্ধাঞ্জলি লিখক",
  "tribute.subtitle": "দেৱালত ওলোৱাৰ আগতে এজন মডাৰেটৰে শ্ৰদ্ধাঞ্জলি পঢ়ে।",
  "tribute.placeholder": "জুবিন দাৰ বাবে দুআষাৰ…",
  "tribute.submit": "পঠিয়াওক",
  "tribute.cancel": "বাতিল কৰক",
  "tribute.queued": "ধন্যবাদ। মডাৰেটৰে অলপ পিছতে চাব। 🪔",
  "tribute.empty": "অনুগ্ৰহ কৰি আগেয়ে কিবা লিখক।",
  "tribute.too_long": "শ্ৰদ্ধাঞ্জলি ১২০টা আখৰতকৈ বেছি হ'ব নোৱাৰে।",
  "tribute.filtered": "অনুগ্ৰহ কৰি আন ধৰণে লিখক — এইটো এখন শ্ৰদ্ধাঞ্জলিৰ দেৱাল।",
  "tribute.invalid_chars": "অনুগ্ৰহ কৰি কেৱল চিহ্নৰ সলনি শব্দ ব্যৱহাৰ কৰক।",
  "tribute.rate_limited": "আপুনি এইমাত্ৰ এটা পঠিয়ালে। অলপ সময় দিয়ক।",
  "tribute.wrong_zone": "শ্ৰদ্ধাঞ্জলি লিখিবলৈ দেৱালৰ ওচৰত থিয় হওক।",
  "tribute.failed": "পঠিয়াব পৰা নগ'ল। অনুগ্ৰহ কৰি পুনৰ চেষ্টা কৰক।",

  // Settings
  "settings.open": "ছেটিংছ",
  "settings.title": "ছেটিংছ",
  "settings.language": "ভাষা",
  "settings.lowBandwidth": "কম ডাটা ম'ড",
  "settings.lowBandwidthHelp": "কম ইফেক্ট। লেহেমীয়া সংযোগত ইয়াক ব্যৱহাৰ কৰক।",
  "settings.close": "সম্পূৰ্ণ",

  // Show phases
  "phase.preshow": "দুৱাৰ এতিয়াও খোলা নাই",
  "phase.gates_open": "দুৱাৰ খোলা হৈছে",
  "phase.opening_ceremony": "আৰম্ভণিৰ অনুষ্ঠান",
  "phase.moment_of_silence": "এক মুহূৰ্তৰ নীৰৱতা",
  "phase.live": "এতিয়া চলি আছে",
  "phase.closing_mayabini": "মায়াবিনী",
  "phase.ended": "অহাৰ বাবে ধন্যবাদ",
  "phase.silenceNote": "অনুগ্ৰহ কৰি স্থিৰ হৈ থাকক। প্ৰতিক্ৰিয়া বন্ধ আছে।",
  "phase.mayabiniNote": "সন্ধিয়াটোৰ শেষ গীত।",
  "phase.held": "আয়োজকসকলে ৰাখিছে",

  // Phool Arpan
  "flower.offer": "ফুল অৰ্পণ কৰক",
  "flower.count": "ফুল অৰ্পিত",
  "flower.countOne": "ফুল অৰ্পিত",
  "flower.wrong_zone": "ফুল অৰ্পণ কৰিবলৈ ফুল অৰ্পণত থিয় হওক।",
  "flower.rate_limited": "এবাৰত এটা ফুল।",

  // Seating
  "seat.sit": "ইয়াত বহক",
  "seat.stand": "উঠি থিয় হওক",
  "seat.occupied": "তাত ইতিমধ্যে কোনোবাই বহি আছে।",
  "seat.too_far": "আসনৰ ওচৰলৈ যাওক।",

  // Prayer
  "pray.start": "মূৰ দোৱাওক",
  "pray.stop": "উঠক",
  "pray.wrong_zone": "প্ৰাৰ্থনাৰ বাবে নামঘৰত সোমাওক।",

  // Sing-along
  "sing.raise": "গাওক",
  "sing.voices": "টা কণ্ঠ গাই আছে",
  "sing.voiceOne": "টা কণ্ঠ গাই আছে",
  "sing.wrong_zone": "গাবলৈ মণ্ডপত থিয় হওক।",
  "sing.silence": "নীৰৱতাৰ সময়ত নহয়।",
  "sing.title": "সমূহীয়া গীত",
  "sing.waiting": "গান আৰম্ভ হ’লে শব্দবোৰ ওলাব।",

  // Radio Garden
  "radio.title": "ৰেডিঅ’ বাগিচা",
  "radio.nowPlaying": "এতিয়া বাজি আছে",
  "radio.onAir": "সম্প্ৰচাৰত",
  "radio.silence": "নীৰৱতা",
  "radio.off": "বন্ধ কৰক",
  "radio.wrong_zone": "ষ্টেচন সলাবলৈ বাগিচাত থিয় হওক।",
  "radio.rate_limited": "কোনোবাই এইমাত্ৰ সলালে। অলপ সময় দিয়ক।",
  "radio.station.bihu": "বিহু",
  "radio.station.geet": "গীত",
  "radio.station.hindi": "হিন্দী",
  "radio.station.bhokti": "ভক্তিগীত",
  "radio.station.borgeet": "বৰগীত",
  "radio.unavailable": "এই ষ্টেচনত এতিয়াও ৰেকৰ্ডিং দিয়া হোৱা নাই।",

  // Memories Theatre
  "theatre.title": "স্মৃতিৰ প্ৰেক্ষাগৃহ",
  "theatre.upNext": "পিছত",

  // Memory Lane
  "lane.title": "সোঁৱৰণীৰ বাট",

  // Direction map
  "map.open": "মানচিত্ৰ",
  "map.title": "আপুনি ক’ত আছে",
  "map.you": "আপুনি",
  "map.close": "বন্ধ কৰক",
  "map.hint": "বাট চাবলৈ এটা কোঠাত টিপক।",
  "map.heading": "যাওক",

  // চাকিৰ প্ৰতিকৃতি আৰু অনন্ত কোৰাচ
  "portrait.title": "প্ৰতিকৃতি",
  "portrait.total": "চাকি প্ৰতিকৃতিত",
  "portrait.totalOne": "চাকি প্ৰতিকৃতিত",
  "portrait.invite": "ইয়াত জ্বলোৱা প্ৰতিটো চাকি তেওঁৰ প্ৰতিকৃতিৰ এক অংশ হয়।",
  "portrait.lit": "আপোনাৰ শিখা",
  "portrait.already": "আপোনাৰ শিখা ইতিমধ্যে জ্বলি আছে —",

  "choir.action": "এটা শাৰী গাওক",
  "choir.title": "অনন্ত কোৰাচ",
  "choir.loading": "আপোনাৰ বাবে এটা শাৰী বিচাৰি আছোঁ…",
  "choir.yourLine": "শাৰী",
  "choir.alreadySinging": "জনে ইতিমধ্যে গাইছে",
  "choir.howto": "এবাৰ পঢ়ক, তাৰ পিছত ৰেকৰ্ড কৰক। পঠিওৱাৰ আগতে শুনিব পাৰিব।",
  "choir.record": "ৰেকৰ্ড কৰক",
  "choir.stop": "হ’ল",
  "choir.retake": "পুনৰ ৰেকৰ্ড কৰক",
  "choir.send": "মোৰ কণ্ঠ পঠিয়াওক",
  "choir.sending": "পঠিয়াই আছোঁ…",
  "choir.thanks": "আপোনাৰ কণ্ঠ কোৰাচত সোমাল।",
  "choir.voiceNumber": "কণ্ঠ",
  "choir.close": "বন্ধ কৰক",
  "choir.noMic": "মাইক্ৰʼফোনৰ অনুমতি নাপালোঁ, গতিকে একো ৰেকৰ্ড হোৱা নাই।",
  "choir.rateLimited": "আপুনি ইতিমধ্যে এটা শাৰী পঠিয়াইছে। অলপ পিছত আহক।",
  "choir.unavailable": "এই মুহূৰ্তত কোৰাচলৈ সংযোগ নাই। একো হেৰোৱা নাই।",
  "choir.failed": "পঠিয়াব পৰা নগ’ল। একো হেৰোৱা নাই — অলপ পিছত চেষ্টা কৰক।",

  "radio.module": "জুবিন ৰেডিঅ’",
  "radio.listeners": "জনে শুনি আছে",
  "radio.upNext": "পিছত",
  "radio.tributeFrom": "ৰেকৰ্ড কৰিছে",

  // Voice
  "voice.ask": "কথা পাতিবলৈ সোধক",
  "voice.inviteTitle": "কথা পাতিব বিচাৰে",
  "voice.accept": "গ্ৰহণ কৰক",
  "voice.decline": "নালাগে",
  "voice.calling": "কথা পাতিবলৈ সুধিছে…",
  "voice.connected": "কথা পাতি আছে",
  "voice.end": "শেষ কৰক",
  "voice.mute": "নিমাত কৰক",
  "voice.unmute": "মাত খোলক",
  "voice.report": "অভিযোগ",
  "voice.reported": "অভিযোগ দিয়া হ’ল। কল শেষ হ’ল।",
  "voice.declined": "তেওঁ অস্বীকাৰ কৰিলে।",
  "voice.timeout": "কোনো উত্তৰ নাই।",
  "voice.left": "তেওঁ গুচি গ’ল।",
  "voice.busy": "আপোনালোকৰ এজন ইতিমধ্যে কথা পাতি আছে।",
  "voice.too_far": "সুধিবলৈ ওচৰলৈ যাওক।",
  "voice.denied": "কথা পাতিবলৈ মাইক্ৰʼফোনৰ অনুমতি লাগে।",
  "voice.failed": "কল সংযোগ হ’ব নোৱাৰিলে।",
  "voice.consent": "কণ্ঠালাপ এজনৰ লগত এজন, আৰু দুয়োজনে সন্মত হ’লেহে আৰম্ভ হয়।",

  // Instagram embeds
  "ig.load": "এই স্মৃতিটো দেখুৱাওক",
  "ig.loading": "ল’ড হৈ আছে…",
  "ig.open": "ইনষ্টাগ্ৰামত খোলক",
  "ig.failed": "এইটো ইয়াত দেখুৱাব পৰা নগ’ল। লিংকটো এতিয়াও কাম কৰে।",
  "ig.lowBandwidth": "কম ডাটা ম’ডত ল’ড কৰা নহয়।",
  "ig.none": "এতিয়াও কোনো স্মৃতি যোগ কৰা হোৱা নাই।",
  "ig.prev": "আগৰটো",
  "ig.next": "পিছৰটো",
  "ig.credit": "ইনষ্টাগ্ৰামত ইয়াৰ গৰাকীসকলে পোষ্ট কৰিছে।",
  "ig.openInWorld": "এই স্মৃতিটো চাওক",
  "lane.photoAlt": "জুবিন দাৰ এটি স্মৃতি, এজন ভক্তই ভাগ কৰিছে",
  "theatre.watch": "এই স্মৃতিটো চাওক",
  "presence.one": "জন আজি ৰাতি ইয়াত আছে",
  "presence.many": "জন আজি ৰাতি ইয়াত আছে",
  "theatre.credit": "zubeengarg.xyz ত ভক্তসকলে পঠিয়াইছে",
  /*
   * Left in English on purpose, in both languages.
   *
   * This one is an attribution rather than a piece of the venue: it names
   * YouTube, it is the answer to "whose films are these", and it is the line
   * anybody checking would expect to find in the same words everywhere. The
   * button that opens it stays in English with it, so a screen reader does not
   * announce the two halves of one footnote in two languages.
   */
  "stage.sourceLabel": "Where these films come from",
  "stage.sourceNote":
    "Nothing here is re-uploaded — every film plays straight from its own YouTube channel, so the views go home.",
  "theatre.stop": "বন্ধ কৰক",
  "lane.credit": "zubeengarg.xyz ত ভক্তসকলে ভাগ কৰিছে",
  "ig.close": "বন্ধ কৰক",
};

export const STRINGS: Record<Locale, Strings> = { en, as };

/** Keys present in English but missing in Assamese, for a build-time audit. */
export function missingKeys(locale: Locale): string[] {
  return Object.keys(en).filter((key) => !STRINGS[locale][key]);
}
